import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  bulkOperationsDrawerAF,
  GlobalAppEventEnum,
  IRydeProcess,
  isProcessCanceledByUser,
  lastSearchCriteriaAtomFamily,
  Log,
  PLURAL_KEY,
  reportBulkOperationsDrawerAF,
  reportLastSearchCriteriaByReportIdAF,
  ReportTemplateIdEnum,
  RydeUiUnit,
  selectedIdsSF,
  selectedSingleRowRideIdAtom,
  selectedTableRowsIdsAtomFamily,
  supportsBulkSummaryToast,
} from "@ryde/shared";

import { useCompanyScopeContext, useDropdownSourceContext } from "../contexts";
import {
  refreshDropdownItemsForEntity,
  shouldRefreshDropdownAfterBulkProcess,
} from "./context-providers/parts";
import { useReportId } from "./reports";
import { useDynamicFormsListActions } from "./use-dynamic-forms-list-actions";
import { useHighlightEdited } from "./use-highlight-edited";
import { useSelectedIds } from "./use-selected-ids";

export const useProcessBulkWatchersEnd = ({
  entity,
  currentEntityPage,
  avoidResetSelectedIds,
}: {
  entity: RydeUiUnit;
  currentEntityPage?: RydeUiUnit;
  avoidResetSelectedIds?: boolean;
}) => {
  const { t } = useTranslation();
  const { eventEmitter } = useCompanyScopeContext();
  const { getDropdownItems } = useDropdownSourceContext();

  const { clearSelectedIds } = useSelectedIds();
  const { setHighlightedRows } = useHighlightEdited(entity);
  const selectedIds = useRecoilValue(selectedIdsSF(entity));
  const setSelectedTableRowsIdsAtomFamily = useSetRecoilState(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const setSelectedSingleRowRideId = useSetRecoilState(
    selectedSingleRowRideIdAtom,
  );
  const { removeRows } = useDynamicFormsListActions(entity);
  const [bulkOperationsDrawerOpened, setBulkOperationsDrawerOpened] =
    useRecoilState(bulkOperationsDrawerAF(entity));

  const setLastSearchCriteria = useSetRecoilState(
    lastSearchCriteriaAtomFamily(entity),
  );

  const { reportId } = useReportId();
  const setRidesReportBulkOperationsDrawerOpened = useSetRecoilState(
    reportBulkOperationsDrawerAF(ReportTemplateIdEnum.RIDE_ANALYTICS_REPORT),
  );
  const setReportLastSearchCriteria = useSetRecoilState(
    reportLastSearchCriteriaByReportIdAF(reportId),
  );

  const isCurrentPageReport = currentEntityPage === "report";

  const addressedFieldsValueOnCompleted = useCallback(
    (process: Partial<IRydeProcess> = {}, isHighlightedRows = true) => {
      if (bulkOperationsDrawerOpened) {
        setBulkOperationsDrawerOpened(false);
      }

      if (isCurrentPageReport) {
        if (!avoidResetSelectedIds) {
          clearSelectedIds();
        }
        setReportLastSearchCriteria(undefined);
        setRidesReportBulkOperationsDrawerOpened(false);
        return;
      }

      const { metadata } = process;
      const successIds = metadata?.metrics?.successIds || [];

      if (isHighlightedRows && successIds.length) {
        setHighlightedRows(successIds);
      }

      // RESET LAST SEARCH CRITERIA
      setLastSearchCriteria(undefined);

      eventEmitter?.emit(GlobalAppEventEnum.PROCESS_COMPLETED);

      if (!avoidResetSelectedIds) {
        const successIdsSet = new Set(successIds || []);
        const nextIds = selectedIds.filter(
          prevId => !successIdsSet.has(prevId),
        );
        setSelectedTableRowsIdsAtomFamily(nextIds);
        if (entity === "ride") {
          setSelectedSingleRowRideId(prev =>
            prev !== undefined && nextIds.includes(prev) ? prev : undefined,
          );
        }
      }
    },
    [
      bulkOperationsDrawerOpened,
      isCurrentPageReport,
      setLastSearchCriteria,
      eventEmitter,
      avoidResetSelectedIds,
      setBulkOperationsDrawerOpened,
      setReportLastSearchCriteria,
      setRidesReportBulkOperationsDrawerOpened,
      clearSelectedIds,
      setHighlightedRows,
      selectedIds,
      setSelectedTableRowsIdsAtomFamily,
      entity,
      setSelectedSingleRowRideId,
    ],
  );

  const onUpdateComplete = useCallback(
    (process: IRydeProcess) => {
      const successIds = process.metadata?.metrics?.successIds;
      const shouldRefreshDropdown = shouldRefreshDropdownAfterBulkProcess({
        isCanceled: isProcessCanceledByUser(process),
        successIds,
        selectedIds,
      });

      addressedFieldsValueOnCompleted(process);
      if (shouldRefreshDropdown) {
        refreshDropdownItemsForEntity({ entity, getDropdownItems });
      }
      if (isProcessCanceledByUser(process)) {
        return;
      }
      if (supportsBulkSummaryToast(entity)) {
        return;
      }
      Log.infoAndToast(
        t("general.ENTITY_SUCCESSFULLY_UPDATED", {
          entity: t(`${entity}.${PLURAL_KEY}`),
        }),
        process.metadata?.payload?.dto,
      );
    },
    [addressedFieldsValueOnCompleted, entity, getDropdownItems, selectedIds, t],
  );

  const onArchiveComplete = useCallback(
    (process?: IRydeProcess) => {
      const successIds = process?.metadata?.metrics?.successIds;
      const idsToRemove =
        isProcessCanceledByUser(process) && successIds?.length
          ? successIds
          : selectedIds;
      const shouldRefreshDropdown = shouldRefreshDropdownAfterBulkProcess({
        isCanceled: isProcessCanceledByUser(process),
        successIds,
        selectedIds,
      });

      if (idsToRemove.length) {
        removeRows(idsToRemove);
      }
      if (shouldRefreshDropdown) {
        refreshDropdownItemsForEntity({ entity, getDropdownItems });
      }
      addressedFieldsValueOnCompleted(process, false);
      if (isProcessCanceledByUser(process)) {
        return;
      }
      if (!supportsBulkSummaryToast(entity)) {
        Log.infoAndToast(
          t("general.ENTITY_SUCCESSFULLY_ARCHIVED", {
            entity: t(`${entity}.${PLURAL_KEY}`),
          }),
        );
      }
    },
    [
      addressedFieldsValueOnCompleted,
      entity,
      getDropdownItems,
      removeRows,
      selectedIds,
      t,
    ],
  );

  return {
    onUpdateComplete,
    onArchiveComplete,
    addressedFieldsValueOnCompleted,
  };
};
